const LS_KEY = "rv:products";
const MAX_PRODUCTS = 40;
const MAX_VARIANTS_PER_PRODUCT = 8;
const COOLDOWN_PRODUCT_MS = 24 * 60 * 60 * 1000; // доба — не рухати, якщо той самий товар
const COOLDOWN_VARIANT_MS = 2 * 60 * 1000; // 2 хв — не дублювати, якщо тикнули той самий варіант

/** Один переглянутий варіант усередині запису товару. */
interface RecentVariant {
  key: string;
  color: string;
  size: string;
  ts: number;
}

/** Запис товару в localStorage (`rv:products`). */
interface RecentProduct {
  id: string;
  ts: number;
  variants: RecentVariant[];
}

/** Пара для головної: товар + найсвіжіший переглянутий варіант. */
export interface RecentlyViewedPair {
  productId: string;
  color: string;
  size: string;
  ts: number;
}

/**
 * Вміст LS не валідується: тип описує ОЧІКУВАНУ форму, а не гарантовану.
 * Тому всі рантайм-гарди нижче (`x && x.id`, `Array.isArray(item.variants)`)
 * лишаються на місці — вони прикривають записи, збережені старими версіями.
 */
function readRaw(): RecentProduct[] {
  if (!import.meta.client) return [];
  try {
    // `?? "null"` — не фікс, а збереження поведінки: JSON.parse(null) у JS
    // дає null, далі Array.isArray відсіює. TS такий виклик не пропускає.
    const arr = JSON.parse(localStorage.getItem(LS_KEY) ?? "null");
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}
function writeRaw(list: RecentProduct[]): void {
  if (!import.meta.client) return;
  localStorage.setItem(LS_KEY, JSON.stringify(list.slice(0, MAX_PRODUCTS)));
}

/** Додати перегляд товару (без варіанта). */
export function addRecentlyViewedProduct(productId: string): void {
  if (!productId) return;
  const now = Date.now();
  const list = readRaw();
  const i = list.findIndex((x) => x && x.id === productId);
  if (i >= 0) {
    if (now - (Number(list[i].ts) || 0) < COOLDOWN_PRODUCT_MS) return;
    const [item] = list.splice(i, 1);
    item.ts = now;
    list.unshift(item);
    writeRaw(list);
    return;
  }
  list.unshift({ id: productId, ts: now, variants: [] });
  writeRaw(list);
}

/** Додати переглянутий варіант (колір/розмір) для товару. */
export function addRecentlyViewedVariant(
  productId: string,
  { color = "", size = "" }: { color?: string; size?: string } = {},
): void {
  if (!productId) return;
  const c = String(color || "");
  const s = String(size || "");
  const key = `${c}|${s}`;
  const now = Date.now();

  const list = readRaw();
  let item = list.find((x) => x && x.id === productId);
  if (!item) {
    item = { id: productId, ts: now, variants: [] };
    list.unshift(item);
  }

  if (!Array.isArray(item.variants)) item.variants = [];
  const vi = item.variants.findIndex((v) => v && v.key === key);

  // кулдаун для того самого варіанту
  if (
    vi >= 0 &&
    now - (Number(item.variants[vi].ts) || 0) < COOLDOWN_VARIANT_MS
  ) {
    writeRaw(list);
    return;
  }

  if (vi >= 0) {
    const [v] = item.variants.splice(vi, 1);
    v.ts = now;
    item.variants.unshift(v);
  } else {
    item.variants.unshift({ key, color: c, size: s, ts: now });
    item.variants = item.variants.slice(0, MAX_VARIANTS_PER_PRODUCT);
  }

  // оновлюємо ts товару — останній перегляд будь-якого варіанту
  item.ts = now;

  // переносимо товар у початок
  const pi = list.findIndex((x) => x && x.id === productId);
  if (pi > 0) {
    list.splice(pi, 1);
    list.unshift(item);
  }

  writeRaw(list);
}

/** Отримати ID товарів (спадання часу). */
export function getRecentlyViewedIds(limit = 16): string[] {
  const list = readRaw()
    .slice()
    .sort((a, b) => Number(b.ts || 0) - Number(a.ts || 0))
    .map((x) => x.id);
  const seen = new Set<string>();
  const out: string[] = [];
  for (const id of list)
    if (!seen.has(id)) {
      seen.add(id);
      out.push(id);
    }
  return out.slice(0, limit);
}

/** Отримати пари {productId, color, size} за спаданням часу перегляду варіантів.
 *  Повертає лише ОДИН (найновіший) варіант на товар, щоб на головній
 *  не дублювались карточки одного й того ж товару.
 */
export function getRecentlyViewedPairs(limit = 16): RecentlyViewedPair[] {
  const raw = readRaw();
  const pairs: RecentlyViewedPair[] = [];
  for (const p of raw) {
    const vars = (Array.isArray(p.variants) ? p.variants : [])
      .slice()
      .sort((a, b) => Number(b.ts || 0) - Number(a.ts || 0));
    // Тільки найсвіжіший варіант для кожного товару
    const v = vars[0];
    if (v) {
      pairs.push({
        productId: p.id,
        color: v.color || "",
        size: v.size || "",
        ts: v.ts || 0,
      });
    } else {
      // Товар без варіантів — все одно додаємо
      pairs.push({
        productId: p.id,
        color: "",
        size: "",
        ts: p.ts || 0,
      });
    }
    if (pairs.length >= limit) break;
  }
  return pairs;
}
